import { useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles, Loader2, Download, ListOrdered, Printer, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

function buildPages(content) {
  const lines = (content || "").split("\n");
  const heads = [];
  for (let i = 0; i < lines.length; i++) if (/^##\s+/.test(lines[i])) heads.push(i);
  const pages = [];
  const firstHead = heads.length ? heads[0] : lines.length;
  const pre = lines.slice(0, firstHead).join("\n").trim();
  if (pre) {
    const t = pre.match(/^#\s+(.+)$/m);
    pages.push({ kind: "preamble", title: t ? t[1].trim() : "Page de titre", raw: pre, secIdx: -1 });
  }
  for (let k = 0; k < heads.length; k++) {
    const start = heads[k];
    const bound = k + 1 < heads.length ? heads[k + 1] : lines.length;
    let end = bound;
    for (let j = start + 1; j < bound; j++) if (/^#\s+/.test(lines[j])) { end = j; break; }
    pages.push({ kind: "section", title: lines[start].replace(/^##\s+/, "").trim(), raw: lines.slice(start, end).join("\n"), secIdx: k });
  }
  return pages;
}

const isLesson = (title) => /^Le[çc]on/i.test(title || "");

function wordCount(raw) {
  const txt = (raw || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_`>#-]/g, " ")
    .trim();
  if (!txt) return 0;
  return txt.split(/\s+/).filter(Boolean).length;
}

function renderMarkdown(raw) {
  const lines = (raw || "").split("\n");
  return lines.map((ln, i) => {
    const s = ln.replace(/\*\*/g, "");
    if (/^\s*(---+|\*\*\*+|___+)\s*$/.test(ln)) return <hr key={i} className="my-4 border-slate-200" />;
    if (/^#\s+/.test(ln)) return <h1 key={i} className="mb-2 text-2xl font-bold text-navy">{ln.replace(/^#\s+/, "")}</h1>;
    if (/^##\s+/.test(ln)) return <h2 key={i} className="mb-3 mt-1 text-xl font-bold text-navy">{ln.replace(/^##\s+/, "")}</h2>;
    if (/^###+\s+/.test(ln)) return <h3 key={i} className="mb-1 mt-4 text-base font-semibold text-[#0E7C7B]">{ln.replace(/^#+\s+/, "")}</h3>;
    if (!s.trim()) return <div key={i} className="h-2" />;
    return <p key={i} className="mb-2 text-[15px] leading-relaxed text-slate-700">{s}</p>;
  });
}

function esc(s) {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function pageToHtml(raw) {
  return (raw || "").split("\n").map((ln) => {
    const s = esc(ln.replace(/\*\*/g, ""));
    if (/^\s*(---+|\*\*\*+|___+)\s*$/.test(ln)) return "<hr/>";
    if (/^#\s+/.test(ln)) return `<h1>${esc(ln.replace(/^#\s+/, ""))}</h1>`;
    if (/^##\s+/.test(ln)) return `<h2>${esc(ln.replace(/^##\s+/, ""))}</h2>`;
    if (/^###+\s+/.test(ln)) return `<h3>${esc(ln.replace(/^#+\s+/, ""))}</h3>`;
    if (!s.trim()) return "";
    return `<p>${s}</p>`;
  }).join("\n");
}

function coverHtml(title, level, teacher) {
  const date = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const rows = [];
  if (level) rows.push(`<div class="cmeta">${esc(level)}</div>`);
  if (teacher) rows.push(`<div class="cmeta">Enseignant&nbsp;: ${esc(teacher)}</div>`);
  rows.push(`<div class="cmeta cdate">${date}</div>`);
  return `<section class="cover">
    <div class="logo"><span class="lbadge">&#9829;</span><span class="brand">Vitalis <b>Digital&nbsp;Store</b></span></div>
    <div class="ctitle">${esc(title)}</div>
    <div class="crule"></div>
    <div class="cblock">${rows.join("")}</div>
  </section>`;
}

function printBooklet(pages, meta, title) {
  const cover = coverHtml(title, meta?.level, meta?.teacher);
  const body = cover + pages.map((p) => `<section class="page brk">${pageToHtml(p.raw)}</section>`).join("\n");
  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>
    @page { margin: 20mm; }
    body { font-family: Georgia, 'Times New Roman', serif; color: #1e293b; line-height: 1.55; }
    h1 { color: #0A2540; font-size: 26px; margin: 0 0 10px; }
    h2 { color: #0A2540; font-size: 20px; margin: 0 0 8px; border-bottom: 2px solid #0E7C7B; padding-bottom: 4px; }
    h3 { color: #0E7C7B; font-size: 15px; margin: 14px 0 4px; }
    p { margin: 0 0 8px; font-size: 13px; }
    hr { border: none; border-top: 1px solid #cbd5e1; margin: 12px 0; }
    .brk { page-break-before: always; }
    .cover { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; min-height: 88vh; }
    .logo { display: flex; align-items: center; gap: 10px; margin-bottom: 60px; }
    .lbadge { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 10px; background: #0A2540; color: #fff; font-size: 20px; }
    .brand { font-family: Georgia, serif; font-size: 22px; font-weight: 700; color: #0A2540; }
    .brand b { color: #38BDF8; font-weight: 700; }
    .ctitle { font-size: 34px; font-weight: 700; color: #0A2540; max-width: 80%; line-height: 1.25; }
    .crule { width: 90px; height: 4px; background: #0E7C7B; border-radius: 3px; margin: 26px 0 30px; }
    .cblock { color: #475569; }
    .cmeta { font-size: 16px; margin: 4px 0; }
    .cdate { color: #94a3b8; font-size: 14px; margin-top: 14px; }
  </style></head><body>${body}</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank");
  if (!w) { URL.revokeObjectURL(url); return; }
  w.addEventListener("load", () => { w.focus(); try { w.print(); } catch { /* noop */ } setTimeout(() => URL.revokeObjectURL(url), 4000); });
}

export function CoursPreview({ open, onClose, content, courseMeta, onRegenerate, regenIdx, onExport, exporting }) {
  const pages = useMemo(() => buildPages(content), [content]);
  const courseTitle = useMemo(() => {
    const pre = pages.find((p) => p.kind === "preamble");
    return (pre && pre.title) || (content.match(/^#\s+(.+)$/m) || [])[1] || "Cours académique";
  }, [pages, content]);
  const counts = useMemo(() => pages.map((p) => wordCount(p.raw)), [pages]);
  const lessonAvg = useMemo(() => {
    const ls = pages.map((p, i) => (isLesson(p.title) ? counts[i] : null)).filter((v) => v != null);
    return ls.length ? ls.reduce((a, b) => a + b, 0) / ls.length : 0;
  }, [pages, counts]);
  const isShort = (i) => isLesson(pages[i]?.title) && lessonAvg > 0 && counts[i] < 0.6 * lessonAvg;

  const [idx, setIdx] = useState(0);
  if (!open) return null;
  const safeIdx = Math.min(idx, Math.max(0, pages.length - 1));
  const page = pages[safeIdx];
  const regenerating = page && page.kind === "section" && regenIdx === page.secIdx;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-white" data-testid="cours-preview">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2 text-navy">
          <ListOrdered size={18} className="text-[#0E7C7B]" />
          <span className="font-semibold">Aperçu paginé du cours</span>
          <span className="text-sm text-slate-400">· Page {safeIdx + 1} / {pages.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => printBooklet(pages, courseMeta, courseTitle)} size="sm" variant="outline" data-testid="cours-preview-print" className="rounded-full border-navy text-xs text-navy">
            <Printer size={13} className="mr-1" /> Imprimer
          </Button>
          <Button onClick={() => onExport("pdf")} disabled={!!exporting} size="sm" variant="outline" data-testid="cours-preview-export-pdf" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">
            {exporting === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} PDF
          </Button>
          <Button onClick={() => onExport("docx")} disabled={!!exporting} size="sm" variant="outline" data-testid="cours-preview-export-docx" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">
            {exporting === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Word
          </Button>
          <button type="button" onClick={onClose} data-testid="cours-preview-close" className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Fermer"><X size={20} /></button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Sommaire */}
        <aside className="hidden w-72 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 p-3 md:block" data-testid="cours-preview-toc">
          {pages.map((p, i) => (
            <button key={i} type="button" onClick={() => setIdx(i)} data-testid={`cours-preview-nav-${i}`}
              className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition ${i === safeIdx ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-200"}`}
              title={p.title}>
              <span className="min-w-0 flex-1 truncate">{p.kind === "preamble" ? "📖 " + p.title : p.title}</span>
              {isShort(i) && <AlertTriangle size={12} className="shrink-0 text-amber-500" />}
              <span className={`shrink-0 text-[10px] ${i === safeIdx ? "text-white/70" : "text-slate-400"}`}>{counts[i]}&nbsp;m</span>
            </button>
          ))}
        </aside>

        {/* Contenu de la page */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-2 sm:px-10 lg:px-16" data-testid="cours-preview-meta">
            <span className="text-xs font-medium text-slate-500" data-testid="cours-preview-wordcount">{counts[safeIdx]} mots</span>
            {isShort(safeIdx) && (
              <span data-testid="cours-preview-short-badge" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                <AlertTriangle size={11} /> Leçon courte — à étoffer
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-10 lg:px-16" data-testid="cours-preview-content">
            <div className="mx-auto max-w-3xl">
              {page ? renderMarkdown(page.raw) : <p className="text-slate-400">Aucun contenu.</p>}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-white px-4 py-3 sm:px-8">
            <Button onClick={() => setIdx((v) => Math.max(0, v - 1))} disabled={safeIdx === 0} variant="outline" size="sm" data-testid="cours-preview-prev" className="rounded-full text-xs">
              <ChevronLeft size={14} className="mr-1" /> Précédent
            </Button>
            {page && page.kind === "section" && (
              <Button onClick={() => onRegenerate(page.secIdx)} disabled={regenIdx !== null} size="sm" data-testid="cours-preview-regen" className="rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0c6a69]">
                {regenerating ? <><Loader2 size={13} className="mr-1 animate-spin" /> Régénération…</> : <><Sparkles size={13} className="mr-1" /> {isLesson(page.title) ? "Régénérer cette leçon" : "Régénérer cette section"}</>}
              </Button>
            )}
            <Button onClick={() => setIdx((v) => Math.min(pages.length - 1, v + 1))} disabled={safeIdx >= pages.length - 1} variant="outline" size="sm" data-testid="cours-preview-next" className="rounded-full text-xs">
              Suivant <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
